import React, { useEffect, useRef, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { PaymentDone, StripeIntentForm, StripeSheet, usd } from './StripePayment';

/**
 * Subscribing, without leaving the app.
 *
 * Two things subscribe to something here and they are the same payment with a
 * different payee: the platform's own membership, and a monthly subscription to
 * one creator at the price that creator set. Both open a Stripe subscription
 * whose first invoice is confirmed by the Payment Element right here, so the
 * flow never hands the browser to checkout.stripe.com — which on the packaged
 * mobile app meant an external browser tab and a cold return trip.
 *
 * Deliberately knows nothing about `UserContext`: `UserContext` renders the
 * paywall that mounts this, and importing back into it would be a cycle. The
 * caller is told it succeeded and decides what to refresh.
 */

const ENDPOINTS = {
    platform: '/api/billing/subscription/platform/',
    creator: '/api/billing/subscription/creator/',
};

// Backend error codes -> something a person can act on. Anything unlisted is
// text Stripe wrote about the card, which is already readable.
const MESSAGE_KEYS = {
    already_subscribed: 'billing.alreadySubscribed',
    plan_unavailable: 'billing.planUnavailable',
    no_price_set: 'billing.noPriceSet',
    subscriptions_closed: 'billing.subscriptionsClosed',
    not_a_creator: 'billing.notACreator',
    self_subscription: 'billing.selfSubscription',
    not_found: 'billing.notFound',
};

function SubscribePanel({ target = 'platform', creator, onSubscribed, onCancel }) {
    const { t } = useTranslation();
    const [config, setConfig] = useState(null);   // { client_secret, subscription_id, … }
    const [error, setError] = useState('');
    const [done, setDone] = useState(null);       // { pending: bool }

    const creatorId = creator?.id;
    const started = useRef(false);

    const start = async () => {
        setError('');
        try {
            const res = await api.post(ENDPOINTS[target],
                target === 'creator' ? { id: creatorId } : {});

            // Already live — an account configured to charge a stored card can
            // get here without a card step at all.
            if (!res.data?.client_secret) {
                if (res.data?.status === 'active' || res.data?.status === 'trialing') {
                    setDone({});
                    onSubscribed?.();
                    return;
                }
                setError(t('billing.startError'));
                return;
            }
            setConfig(res.data);
        } catch (err) {
            const code = err.response?.data?.error;
            setError(MESSAGE_KEYS[code] ? t(MESSAGE_KEYS[code]) : (code || t('billing.startError')));
        }
    };

    // Exactly once per mount. Opening a subscription is a write, and the
    // callbacks this component is given are redefined by the parent on every
    // render — a dependency array over them would post a fresh subscription
    // each time the page re-rendered underneath the sheet.
    useEffect(() => {
        if (started.current) return;
        started.current = true;
        start();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Stripe says the card cleared; tell the backend so the screen is right now. */
    const settle = async () => {
        try {
            const res = await api.post('/api/billing/subscription/confirm/', {
                subscription_id: config.subscription_id,
            });
            // Paid but not yet reported as active: the webhook still lands it,
            // so say so rather than implying the money went nowhere.
            setDone({ pending: !res.data?.active });
        } catch (err) {
            setDone({ pending: true });
        }
        onSubscribed?.();
        return null;
    };

    const price = config?.amount_usd ?? creator?.price;
    const forCreator = target === 'creator';

    if (done) {
        return (
            <PaymentDone
                title={t('billing.subscribedTitle')}
                detail={forCreator && creator?.username
                    ? t('billing.subscribedToCreator').replace('{name}', creator.username)
                    : t('billing.subscribedDetail')}
                warning={done.pending ? t('billing.subscriptionPending') : ''}
                onClose={onCancel}
                closeLabel={t('billing.continue')}
            />
        );
    }

    if (error) {
        return (
            <div className="space-y-3">
                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2 leading-relaxed">
                    {error}
                </p>
                <div className="flex gap-2">
                    {onCancel && (
                        <button type="button" onClick={onCancel} className="btn-secondary flex-1 py-2.5 text-sm">
                            {t('billing.close')}
                        </button>
                    )}
                    <button type="button" onClick={start} className="btn-primary flex-1 py-2.5 text-sm">
                        {t('billing.retry')}
                    </button>
                </div>
            </div>
        );
    }

    if (!config) {
        return (
            <div className="flex items-center justify-center py-10">
                <span className="spinner" role="status" />
            </div>
        );
    }

    return (
        <StripeIntentForm
            clientSecret={config.client_secret}
            publishableKey={config.publishable_key}
            amountUsd={price}
            amountLabel={t('billing.perMonth')}
            submitLabel={t('billing.subscribeNow')}
            onSucceeded={settle}
            onCancel={onCancel}
            note={t('billing.recurringNote')}
        >
            {forCreator && creator?.username && (
                <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 leading-relaxed">
                    {t('billing.creatorTerms')
                        .replace('{name}', creator.username)
                        .replace('{price}', usd(price))}
                </p>
            )}
        </StripeIntentForm>
    );
}

/** The same flow in a sheet, for callers with no card of their own to sit in. */
function SubscribeModal({ target = 'creator', creator, onClose, onSubscribed }) {
    const { t } = useTranslation();
    return (
        <StripeSheet
            title={target === 'creator' && creator?.username
                ? t('billing.subscribeToTitle').replace('{name}', creator.username)
                : t('payments.subscribeTitle')}
            subtitle={t('billing.subscribeHint')}
            onClose={onClose}
        >
            <SubscribePanel
                target={target}
                creator={creator}
                onSubscribed={onSubscribed}
                onCancel={onClose}
            />
        </StripeSheet>
    );
}

export default SubscribePanel;
export { SubscribeModal };
